import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";

import { apiPaths, customerPath, customerSchema, type Customer, type CustomerRequest } from "@ravnar/api-contract";

import { ApiError, apiRequest } from "./client";

// The Directory's customers — who the business sells to, and who it is still hoping to. Every query key starts with
// "customers", so a change to one can refresh the kind on its own, while the Directory's own list is a projection under
// "directory" and is invalidated beside it.
//
// There is no reader for `GET /api/customers` here, for the reason `api/other-parties.ts` gives: the Directory lists
// parties off the projection, a page at a time, and a customer's own page reads that one customer. The route answers
// the whole list for the tests and for whatever asks next; a query nothing calls would only be an unused export, which
// `knip` refuses.

/** Adds a customer. Fails with 400 when the form's values are refused and 403 for anyone but an editor. */
export function addCustomer(request: CustomerRequest): Promise<Customer> {
	return apiRequest(apiPaths.customers, customerSchema, { method: "POST", body: request });
}

/**
 * One customer of the organization, by id: what its page opens with, including its note. Only members with access to
 * the Directory may read it; the API answers everyone else with 403 and a customer that is gone with 404.
 */
export const customerQueryOptions = (customerId: string) =>
	queryOptions({
		queryKey: ["customers", "customer", customerId],
		queryFn: ({ signal }) => apiRequest(customerPath(customerId), customerSchema, { signal }),
		// A refused member or a customer that is gone will not come back on a retry; the page acts on it at once.
		retry: (failureCount, error) => failureCount < 1 && !(error instanceof ApiError && error.status < 500),
	});

/**
 * Saves a customer's whole record, its note included. Fails with 400 when the values are refused, 403 for anyone but
 * an editor of the Directory, and 404 when the customer is gone.
 */
export function updateCustomer(customerId: string, request: CustomerRequest): Promise<Customer> {
	return apiRequest(customerPath(customerId), customerSchema, { method: "PATCH", body: request });
}

/** Removes a customer for good; the audit log keeps what became of it. Fails with 403 and 404 as above. */
export async function removeCustomer(customerId: string): Promise<void> {
	await apiRequest(customerPath(customerId), z.undefined(), { method: "DELETE" });
}
